import React, { useCallback, useMemo, useState } from 'react';
import classnames from 'classnames';
import { escape, unescape } from 'lodash-es';

import { Message, SpanScore, Tag, User } from '../../../state/types';
import { createElement } from '../../../../../../services/create-element';
import { ANIMATION, FRAME, IMAGE, LINK, TITLE, VIDEO } from '../../../../../moderation/models/message-entities/types';
import {
  INVALID_CHARACTERS,
  PUBLISH_AND_MODERATE_WORDS,
  REJECTED_WORDS,
  RESTRICTED_WORDS
} from '../../../../../moderation/models/moderation';
import ErrorNotifier from '../../../../../../services/error-notifier';
import { stripPTag } from '../../../utils/domUtils';
import { useInitConfig } from '../../../../../../init-config/useInitConfig';

import FrameEntity from './entities/frame';
import VideoEntity from './entities/video';
import ImageEntity from './entities/image';
import LinkEntity from './entities/link';
import { hasCharWithoutModifiers, hasEmojiWithSkinTones, wrapEmojis } from './messageContentUtils';

const INVALID_TEXT_PATTERN = new RegExp(/([~`!#$%\^&*+=\-\[\]\\';,/{}()|\\":<>\?@])/g);

interface Props {
  message: Message;
  isSearchedByContent: boolean;
  users?: Record<string, User>;
  isCollapsed: boolean;
  setIsCollapsed?: (value: boolean) => void;
}

const defaultProps = {
  message: new Message(),
  isSearchedByContent: false,
  users: {},
  isCollapsed: false
};

const MessageContent: React.FC<Props> = ({
  message,
  isSearchedByContent,
  users,
  isCollapsed,
  setIsCollapsed
} = defaultProps) => {
  // Read more / less options
  const [shouldCollapse, setShouldCollapse] = useState<boolean>(false);
  const { ltr } = useInitConfig();

  const hasRestrictedWords = useMemo(
    () =>
      message
        .get('tags', [])
        .some(tag =>
          [
            RESTRICTED_WORDS,
            REJECTED_WORDS,
            PUBLISH_AND_MODERATE_WORDS,
            INVALID_CHARACTERS,
            Tag.DOXXING
          ].includes(tag)
        ),
    [message]
  );

  const decodeEntity = (input: string) =>
    input.replace(/&(amp|lt|gt|\w+);|&#(\d+);/g, (_, name, dec) =>
      name
        ? name === 'amp'
          ? '&'
          : name === 'lt'
          ? '<'
          : name === 'gt'
          ? '>'
          : _
        : String.fromCharCode(dec)
    );

  const encodeEntity = (input: string) => input.replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const highlightedWords = useMemo(
    () => ({
      restricted: hasRestrictedWords ? message.getRestrictedWords() : [],
      searched: message.get('highlightedWords', [])
    }),
    [message]
  );

  const highlightedTagSpans = useMemo(() => {
    return message.get('highlightedTagSpans', {});
  }, [message]);

  // Construct text and media entities out of message object.
  const entities = useMemo(() => {
    return message.get('entities', []).map((entity, index: number) => {
      switch (entity.type) {
        case IMAGE:
          return <ImageEntity key={`${entity}${index}`} entity={entity} />;
        case ANIMATION:
          return <ImageEntity key={`${entity}${index}`} entity={entity} />;
        case LINK:
          return <LinkEntity key={`${entity}${index}`} entity={entity} />;
        case VIDEO:
          return <VideoEntity key={`${entity}${index}`} entity={entity} />;
        case FRAME:
          return <FrameEntity key={`${entity}${index}`} entity={entity} />;
      }
    });
  }, [message]);

  // Given the message text content highlight words according to type
  const highlightWord = useCallback(
    (type: 'restricted' | 'searched') => (text: string, word: string) => {
      const isEmpty = word.length === 0;

      if (isEmpty) return text;

      const shouldHighlight = highlightedWords?.[type]?.includes(word);

      function wrapMatch(match) {
        return `<span ${shouldHighlight ? `data-${type}-word=${type}-words` : ''}>${encodeEntity(
          match
        )}</span>`;
      }

      let formattedWord = word;
      try {
        if (INVALID_TEXT_PATTERN.test(formattedWord)) {
          formattedWord = formattedWord.replace(INVALID_TEXT_PATTERN, '\\$1');
        }

        if (hasEmojiWithSkinTones(word) && !hasCharWithoutModifiers(word)) {
          return wrapEmojis(text, word, wrapMatch);
        }

        // escape characters that are special in regexp
        formattedWord = formattedWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

        const hasHref = text.includes('href=');
        const linkRegExp = new RegExp(`(?<!href="[^"]*)\\b(${formattedWord})\\b(?!">)`, 'gi');
        const generalRegExp = new RegExp(`(?<![a-zA-Z])${formattedWord}(?![a-zA-Z])`, 'gi');

        const regExp = hasHref ? linkRegExp : generalRegExp;

        return text.replace(regExp, (match, offset) => {
          return wrapMatch(match);
        });
      } catch (error) {
        console.error(error);
        ErrorNotifier.notifyError(error);
        return text;
      }
    },
    [message]
  );

  const highlightTagSpan = useCallback(
    (text: string) => {
      if (text.length === 0) return text;

      const selectedTag = highlightedTagSpans?.selectedTagName;
      const formattedSpan: string[] = highlightedTagSpans?.spanScoreResults?.[selectedTag]?.map(
        (span: SpanScore) => {
          // Unescaping to normal text and removing whitespaces from withing the string and around it then returing the string to original string with escape
          // We do this because the spans indexes are for normal text only
          const normalizedText = stripPTag(text);

          let str = unescape(normalizedText).trim();
          str = str.slice(span.begin, span.end);
          str = escape(str);
          return str;
        }
      );

      try {
        formattedSpan?.map(textToReplace => {
          text = text.replace(
            textToReplace,
            `<span data-text-analyzer-tag-span="text-analyzer-tag-span">${textToReplace}</span>`
          );
        });
        return text;
      } catch (error) {
        console.error(error);
        ErrorNotifier.notifyError(error);
        return text;
      }
    },
    [message]
  );

  const [textEntities, titleEntity]: [React.ReactNodeArray, React.ReactElement] = useMemo(() => {
    let titleEntity = null;
    return [
      message
        .get('textEntities', [])
        .map((textEntity: any) => {
          const messageTextContent: string = decodeEntity(textEntity.text);
          const className = `${textEntity.type}-entity`;
          let formattedMessageContent: string = messageTextContent;

          if (Object.keys(highlightedTagSpans).length > 0) {
            formattedMessageContent = highlightTagSpan(formattedMessageContent);
          }

          if (hasRestrictedWords) {
            formattedMessageContent = highlightedWords.restricted.reduce(
              highlightWord('restricted'),
              formattedMessageContent
            );
          }

          if (isSearchedByContent) {
            formattedMessageContent = highlightedWords.searched.reduce(
              highlightWord('searched'),
              formattedMessageContent
            );
          }

          // TODO: think what we are doing with users ?
          const [entityComponent, length]: [React.ReactNode, number] = createElement(
            React.createElement,
            formattedMessageContent,
            className,
            users
          ) as [React.ReactNode, number];

          if (textEntity.type === TITLE) {
            titleEntity = entityComponent;
            return null;
          }
          setShouldCollapse(length >= 500);

          return entityComponent;
        })
        .filter(entityComponent => entityComponent),
      titleEntity
    ];
  }, [message]);

  const rtlAttr = ltr ? {} : { dir: 'rtl' };

  return (
    <>
      {titleEntity}
      <div
        className={classnames('truncated-wrapper', { collapsed: isCollapsed })}
        data-testid={'message-content'}
        {...rtlAttr}
      >
        {textEntities}
        {shouldCollapse && (
          <div
            className={classnames('overlay-read-more', isCollapsed ? 'collapsed' : 'expanded')}
          ></div>
        )}
      </div>
      {entities}
    </>
  );
};

export default MessageContent;
